import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { Product } from '../types';
import { CartService } from '../services/CartService';
import { PartnerService } from '../services/PartnerService';

interface CartItem {
  product: Product;
  quantity: number;
}

interface PartnerOrder {
  partnerId: number;
  partnerName: string;
  items: CartItem[];
  total: number;
  paymentMethod?: string;
  status: 'pending' | 'paid';
}

interface CartContextType {
  items: CartItem[];
  partnerOrders: PartnerOrder[];
  setPartnerOrders: React.Dispatch<React.SetStateAction<PartnerOrder[]>>;
  activePartnerId: number | null;
  addToCart: (
    product: Product,
    quantity?: number
  ) => Promise<{
    success: boolean;
    isNewPartner?: boolean;
    message?: string;
    items?: CartItem[];
  }>;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  setActivePartner: (partnerId: number) => void;
  updatePartnerPaymentMethod: (partnerId: number, method: string) => void;
  total: number;
  isLoading: boolean;
  partnerIds: number[];
  clearPartnerCart: (partnerId: number) => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Get unique partner IDs from current cart items 
  //Used To detect if a new partners is added + in the orders to generate unique order for each partner
  const partnerIds = useMemo(() => {
    const partners = new Set<number>();
    items.forEach(item => {
      if (item.product.partner_id) {
        partners.add(item.product.partner_id);
      }
    });
    return Array.from(partners);
  }, [items]);

  const [partnerOrders, setPartnerOrders] = useState<PartnerOrder[]>([]);
  const [activePartnerId, setActivePartnerId] = useState<number | null>(0);

  // Group items by partner whenever items change
  useEffect(() => {
  const groupedOrders = groupItemsByPartner(items);

  // Merge paymentMethod from old partnerOrders if exists (to conserve the payment method)
  const mergedOrders = groupedOrders.map(order => {
    const existing = partnerOrders.find(po => po.partnerId === order.partnerId);
    return {
      ...order,
      paymentMethod: existing?.paymentMethod || order.paymentMethod || undefined,
    };
  });

  setPartnerOrders(mergedOrders);

  // Set active partner if not set or current active partner no longer exists
  if (!activePartnerId || !mergedOrders.some(o => o.partnerId === activePartnerId)) {
    setActivePartnerId(mergedOrders[0]?.partnerId || 0);
  }
}, [items]);


  const groupItemsByPartner = (cartItems: CartItem[]): PartnerOrder[] => {
    const partnerMap = new Map<number, PartnerOrder>();
    const NO_PARTNER_ID = 0;  // Special ID for no-partner items

    cartItems.forEach(item => {
        const partnerId = item.product.partner_id ?? NO_PARTNER_ID;
        
        if (!partnerMap.has(partnerId)) {
            partnerMap.set(partnerId, {
              partnerId,
              partnerName: partnerId === NO_PARTNER_ID 
                ? 'No Partner' 
                : `Partner ${item.product.partner_info?.nom_entreprise
                  ? item.product.partner_info.nom_entreprise.replace(/\b\w/g, (c:any) => c.toUpperCase())
                  : String(partnerId)
                }`,
              items: [],
              total: 0,
              status: 'pending'
            });
        }

        const order = partnerMap.get(partnerId)!;
        order.items.push(item);
        order.total += item.product.price * item.quantity;
    });

    return Array.from(partnerMap.values());
};

  const setActivePartner = (partnerId: number) => {
    setActivePartnerId(partnerId);
  };

  const updatePartnerPaymentMethod = (partnerId: number, method: string) => {
    setPartnerOrders(prev =>
      prev.map(order =>
        order.partnerId === partnerId
          ? { ...order, paymentMethod: method }
          : order
      )
    );
  };

  // Get user ID from localStorage if logged in
  const getUserId = useCallback(() => {
    const userInfoRaw = localStorage.getItem('userInfo');
    const userEmail = localStorage.getItem('userEmail');
    // console.log('[CartContext] Raw userInfo from localStorage:', userInfoRaw);
    // console.log('[CartContext] userEmail from localStorage:', userEmail);

    // If no userEmail, user is definitely not logged in
    if (!userEmail) {
      // console.log('[CartContext] No userEmail found - user is not logged in');
      return undefined;
    }

    if (!userInfoRaw) {
      // console.log('[CartContext] No userInfo found, but userEmail exists. User might be logged in but userInfo not set yet.');
      return undefined;
    }

    try {
      const userInfo = JSON.parse(userInfoRaw);
      // console.log('[CartContext] Parsed userInfo:', userInfo);
      // console.log('[CartContext] userInfo keys:', Object.keys(userInfo));

      // Handle both direct user info structure and nested utilisateur structure
      const userId = userInfo?.id || userInfo?.utilisateur?.id;
      // console.log('[CartContext] Extracted userId:', userId);
      // console.log('[CartContext] userInfo.id:', userInfo?.id);
      // console.log('[CartContext] userInfo.utilisateur?.id:', userInfo?.utilisateur?.id);

      return userId ? Number(userId) : undefined;
    } catch (error) {
      console.error('[CartContext] Error parsing userInfo:', error);
      return undefined;
    }
  }, []);

  // Fetch cart from backend and map to product details
  const fetchCart = useCallback(async (userId: number) => {
    console.log('Fetching ');
    setIsLoading(true);
    try {
      const role = JSON.parse(localStorage.getItem('Role') || '""');
      if (role !== 'client') {
        setItems([]);
        setIsLoading(false);
        return;
      }
      // console.log(`[CartContext] Fetching cart for userId: ${userId}`);

      // Fetch cart first
      const cart = await CartService.getCartByUser(userId);
      console.log(`[CartContext] Successfully fetched cart for userId: ${userId}`, cart);

      // Fetch products with error handling
      // let products: Product[] = [];
      const cartItems = cart.items || [];
      const products = cartItems.map(item => ({
        ...item.product,
        quantity: item.quantity,  // include quantity from cart item
        cartItemId: item.id       // optionally track cart item ID
      }));

      // try {
      //   const productsResponse = await PartnerService.getProducts();
      //   if (Array.isArray(productsResponse)) {
      //     products = productsResponse;
      //   } else if (productsResponse && Array.isArray((productsResponse as any).products)) {
      //     products = (productsResponse as any).products;
      //   } else {
      //     products = [];
      //   }
      //   // console.log(`[CartContext] Fetched ${products.length} products`);
      // } catch (productsError) {
      //   console.error('[CartContext] Failed to fetch products:', productsError);
      //   products = []; // Use empty array as fallback
      // }

      // console.log(`[CartContext] Cart has ${cart.items?.length || 0} items`);
      // console.log(`[CartContext] Products sample:`, products.slice(0, 3));

      // Map backend cart items to full product details, only if product exists
      const backendItems = (cart.items || [])
        .map(item => {
          const product = products.find((p: Product) => p.id === item.product_id);
          if (!product) {
            console.warn(`[CartContext] Product not found for product_id: ${item.product_id}`);
            return null;
          }
          return {
            product,
            quantity: item.quantity
          };
        })
        .filter(Boolean); // Remove nulls

      console.log(`[CartContext] Successfully mapped ${backendItems.length} cart items from database`);
      setItems(backendItems as CartItem[]);
      localStorage.setItem('cartItems', JSON.stringify(backendItems));
    } catch (error) {
      console.error('[CartContext] Failed to fetch cart from database:', error);
      // Only use local storage fallback for non-logged-in users
      const userId = getUserId();
      if (!userId) {
        const localCart = localStorage.getItem('cartItems');
        if (localCart) {
          try {
            const parsedCart = JSON.parse(localCart);
            console.log(`[CartContext] Using fallback local cart with ${parsedCart.length} items for non-logged-in user`);
            setItems(parsedCart);
          } catch (parseError) {
            console.error('[CartContext] Failed to parse local cart:', parseError);
            setItems([]);
          }
        } else {
          setItems([]);
        }
      } else {
        // For logged-in users, if database fetch fails, show empty cart
        console.log('[CartContext] Database fetch failed for logged-in user, showing empty cart');
        setItems([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [getUserId]);

  // On mount: fetch cart from backend or localStorage
  useEffect(() => {
    const initializeCart = () => {
      const userId = getUserId();
      const userEmail = localStorage.getItem('userEmail');

      // console.log(`[CartContext] Initializing cart for userId: ${userId}, userEmail: ${userEmail}`);

      if (userId && userEmail) {
        const role = JSON.parse(localStorage.getItem('Role') || '""');
        if (role === 'client') {
          // User is logged in, fetch cart from backend
          fetchCart(userId);
        }
      } else {

        setItems([]);
        localStorage.removeItem('cartItems');
        setIsLoading(false);
      }
    };

    // Initial attempt
    initializeCart();

    // If no user ID found initially, retry after a short delay (in case of timing issues)
    const userId = getUserId();
    const userEmail = localStorage.getItem('userEmail');
    if (!userId && userEmail) {
      // console.log('[CartContext] User email found but no userInfo yet, retrying in 1 second...');
      const retryTimer = setTimeout(() => {
        const retryUserId = getUserId();
        if (retryUserId) {
          console.log(`[CartContext] Retry successful, found userId: ${retryUserId}`);
          const role = JSON.parse(localStorage.getItem('Role') || '""');
          if (role === 'client') {
            fetchCart(retryUserId);
          }
        } else {
          // console.log('[CartContext] Retry failed, still no userId found');
          // Clear cart if retry fails
          setItems([]);
          localStorage.removeItem('cartItems');
          setIsLoading(false);
        }
      }, 1000);

      return () => clearTimeout(retryTimer);
    }
  }, [getUserId, fetchCart]);

  // Add or update a product in the cart
  const addToCart = useCallback(async (product: Product, quantity: number = 1): Promise<{
    success: boolean;
    isNewPartner?: boolean;
    message?: string;
  }> => {
    const isPrescription = product.id === 'prescription';
    const userId = getUserId();

    // Handle product with quantity property (legacy support)
    const actualQuantity = (product as any).quantity || quantity;

    console.log(`[CartContext] Adding product to cart: ${product.id}, quantity: ${actualQuantity}, userId: ${userId}`);



    // Check for new partner before optimistic update
    const currentPartnerIds = Array.from(new Set(
      items.filter(item => item.product.partner_id).map(item => item.product.partner_id as number)
    ));
    const isNewPartner = !!product.partner_id &&
      !currentPartnerIds.includes(product.partner_id) &&
      currentPartnerIds.length > 0;

    // Optimistic update for immediate UI feedback
    setItems(currentItems => {
      const existingItem = currentItems.find(item => item.product.id === product.id);
      if (isPrescription && existingItem) return currentItems;
      const finalQuantity = isPrescription ? 1 : actualQuantity;
      let newItems;
      if (existingItem) {
        newItems = currentItems.map(item =>
          item.product.id === product.id
            ? { ...item, quantity: isPrescription ? 1 : item.quantity + finalQuantity }
            : item
        );
      } else {
        const cleanProduct: Product = {
          id: product.id,
          name: product.name,
          description: product.description || '',
          price: product.price,
          imageData: product.imageData,
          imageMimeType: product.imageMimeType,
          category: product.category,
          inStock: product.inStock !== undefined ? product.inStock : true,
          createdAt: product.createdAt || new Date().toISOString(),
          updatedAt: product.updatedAt || new Date().toISOString(),
          partner_id: product.partner_id || undefined,
          partner_info: product.partner_info || undefined
        };
        newItems = [...currentItems, { product: cleanProduct, quantity: finalQuantity }];
      }

      localStorage.setItem('cartItems', JSON.stringify(newItems));
      return newItems;
    });

    // Prepare base return value
    const returnValue = {
      success: true,
      isNewPartner,
      message: isNewPartner
        ? "This product is from a different partner than others in your cart"
        : undefined
    };
    console.log("message:", returnValue);
    console.log("current partner:", product.partner_id);
    console.log('partnerIds', partnerIds);

    // Sync with backend if logged in
    if (userId) {
      const role = JSON.parse(localStorage.getItem('Role') || '""');
      if (role === 'client') {
        try {
          // console.log(`[CartContext] Syncing add to cart with backend for userId: ${userId}`);
          await CartService.addToCart(userId, product.id, actualQuantity);

          // Don't refresh the entire cart to preserve item positions
          // The optimistic update above already handles the UI correctly
        } catch (error) {
          console.error('[CartContext] Failed to sync add to cart with backend:', error);
          await fetchCart(userId); // fallback
          return {
            ...returnValue,
            success: false,
            message: "Failed to sync cart with server"
          };
        }
      }
    }

    return returnValue;
  }, [getUserId, fetchCart, items]);

  // Remove a product from the cart
  const removeFromCart = useCallback(async (productId: string) => {
    const userId = getUserId();
    // console.log(`[CartContext] Removing product from cart: ${productId}, userId: ${userId}`);

    // Optimistic update
    setItems(currentItems => {
      const newItems = currentItems.filter(item => item.product.id !== productId);
      localStorage.setItem('cartItems', JSON.stringify(newItems));
      return newItems;
    });

    // Sync with backend if logged in
    if (userId) {
      const role = JSON.parse(localStorage.getItem('Role') || '""');
      if (role === 'client') {
        try {
          // console.log(`[CartContext] Syncing remove from cart with backend for userId: ${userId}`);
          await CartService.removeFromCart(userId, productId);
          // Don't refresh the entire cart to preserve item positions
          // The optimistic update above already handles the UI correctly
        } catch (error) {
          console.error('[CartContext] Failed to sync remove from cart with backend:', error);
          // Refresh from backend to restore correct state
          await fetchCart(userId);
        }
      }
    }
  }, [getUserId, fetchCart]);

  // Update the quantity of a product in the cart
  const updateQuantity = useCallback(async (productId: string, quantity: number) => {
    const userId = getUserId();
    // console.log(`[CartContext] Updating quantity for product: ${productId}, quantity: ${quantity}, userId: ${userId}`);

    // Optimistic update
    setItems(currentItems => {
      const newItems = currentItems
        .map(item => {
          if (item.product.id === 'prescription' && item.product.id === productId) {
            return { ...item, quantity: 1 };
          }
          return item.product.id === productId
            ? { ...item, quantity: Math.max(0, quantity) }
            : item;
        })
        .filter(item => item.quantity > 0);

      localStorage.setItem('cartItems', JSON.stringify(newItems));
      return newItems;
    });

    // Sync with backend if logged in
    if (userId) {
      const role = JSON.parse(localStorage.getItem('Role') || '""');
      if (role === 'client') {
        try {
          if (quantity <= 0) {
            // console.log(`[CartContext] Removing item due to zero quantity: ${productId}`);
            await CartService.removeFromCart(userId, productId);
          } else {
            // Get current cart to find existing quantity
            const currentCart = await CartService.getCartByUser(userId);
            const existingItem = currentCart.items?.find(item => item.product_id === productId);
            const currentQuantity = existingItem?.quantity || 0;

            if (quantity > currentQuantity) {
              // Need to add more
              const quantityToAdd = quantity - currentQuantity;
              // console.log(`[CartContext] Adding ${quantityToAdd} more to reach target quantity ${quantity}`);
              await CartService.addToCart(userId, productId, quantityToAdd);
            } else if (quantity < currentQuantity) {
              // Need to remove some and re-add the target quantity
              // console.log(`[CartContext] Removing item and re-adding with target quantity ${quantity}`);
              await CartService.removeFromCart(userId, productId);
              if (quantity > 0) {
                await CartService.addToCart(userId, productId, quantity);
              }
            } else {
              // console.log(`[CartContext] Quantity already at target: ${quantity}`);
            }
          }

          // Don't refresh the entire cart to preserve item positions
          // Only update localStorage to keep it in sync
          const updatedItems = items
            .map(item => {
              if (item.product.id === productId) {
                return { ...item, quantity: Math.max(0, quantity) };
              }
              return item;
            })
            .filter(item => item.quantity > 0);

          localStorage.setItem('cartItems', JSON.stringify(updatedItems));
        } catch (error) {
          console.error('[CartContext] Failed to sync quantity update with backend:', error);
          // Only refresh from backend on error to restore correct state
          await fetchCart(userId);
        }
      }
    }
  }, [getUserId, items]);

  useEffect(() => {
    console.log('items FOR TEST:', items);
  }, [items]);

const clearCart = useCallback(async () => {
  const pendingOrderItems = JSON.parse(localStorage.getItem('pendingOrderItems') || '[]');
  const pendingIds = new Set(pendingOrderItems.map((item: any) => String(item.product.id)));


  const userId = getUserId();
  if (userId) {
    const role = JSON.parse(localStorage.getItem('Role') || '""');
    if (role === 'client') {
      try {
        // Remove from backend first
        await Promise.all([...pendingIds].map(id => removeFromCart(String(id))));
        console.log('[CartContext] Successfully cleared pending items from backend');
        // Then refresh cart from backend
        // await fetchCart(userId);

        console.log('[CartContext] Successfully cleared pending items and refreshed cart');
      } catch (error) {
        console.error('[CartContext] Failed to clear pending items from backend:', error);
        await fetchCart(userId); // Still refresh to restore correct state
      }
    }
  } else {
    // For non-logged-in users, just clear locally
    setItems([]);
    localStorage.setItem('cartItems', JSON.stringify([]));
  }
}, [getUserId, fetchCart]);



  const clearPartnerCart = useCallback(async (partnerId: number) => {
    const userId = getUserId();

    // Filter local cart
    setItems(prevItems => {
      const updatedItems = prevItems.filter(item => item.product.partner_id !== partnerId);
      localStorage.setItem('cartItems', JSON.stringify(updatedItems));
      return updatedItems;
    });

    if (userId) {
      const role = JSON.parse(localStorage.getItem('Role') || '""');
      if (role === 'client') {
        try {
          const cart = await CartService.getCartByUser(userId);
          console.log(`[CartContext] Clearing cart items for userId: ${userId}`, cart);
          if (cart.items && cart.items.length > 0) {
            const partnerItems = cart.items.filter(item => item.product.partner_id === partnerId);
            console.log(`[CartContext] Clearing cart items for partner ${partnerId}`, partnerItems);
            await Promise.all(
              partnerItems.map(item =>
                CartService.removeFromCart(userId, item.product_id)
              )
            );
          }
          console.log(`[CartContext] Cleared cart items for partner ${partnerId}`);
        } catch (error) {
          console.error('[CartContext] Failed to clear partner cart on backend:', error);
        }
      }
    }
  }, [getUserId]);


  const total = items.reduce((sum, item) => sum + item.product.price * item.quantity, 0);

  // Listen for user login/logout changes
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'userInfo' || event.key === 'userEmail') {
        const userId = getUserId();
        const userEmail = localStorage.getItem('userEmail');

        // console.log(`[CartContext] User info changed, userId: ${userId}, userEmail: ${userEmail}`);

        if (userId && userEmail) {
          const role = JSON.parse(localStorage.getItem('Role') || '""');
          if (role === 'client') {
            // User is logged in, fetch their cart
            fetchCart(userId);
          }
        } else {
          // User logged out or no longer authenticated, clear cart
          console.log('[CartContext] User logged out or not authenticated, clearing cart');
          setItems([]);
          localStorage.removeItem('cartItems');
          setIsLoading(false);
        }
      }
    };

    const handleLogout = () => {
      const role = JSON.parse(localStorage.getItem('Role') || '""');
      if (role === 'client') {
        console.log('[CartContext] Logout event detected, clearing cart');
      }
      setItems([]);
      localStorage.removeItem('cartItems');
      setIsLoading(false);
    };

    const handleLogin = () => {
      console.log('[CartContext] Login event detected, fetching user cart');
      const userId = getUserId();
      const userEmail = localStorage.getItem('userEmail');

      console.log(`[CartContext] Login check - userId: ${userId}, userEmail: ${userEmail}`);

      if (userId && userEmail) {
        const role = JSON.parse(localStorage.getItem('Role') || '""');
        if (role === 'client') {
          // User info is immediately available, fetch cart
          console.log(`[CartContext] User info available, fetching cart for userId: ${userId}`);
          fetchCart(userId);
        } else {
          // User info might not be immediately available, retry after a short delay
          console.log('[CartContext] User info not immediately available, retrying in 500ms');
          setTimeout(() => {
            const retryUserId = getUserId();
            const retryUserEmail = localStorage.getItem('userEmail');

            console.log(`[CartContext] Retry check - userId: ${retryUserId}, userEmail: ${retryUserEmail}`);

            if (retryUserId && retryUserEmail) {
              console.log(`[CartContext] Retry successful, found userId: ${retryUserId}`);
              const retryRole = JSON.parse(localStorage.getItem('Role') || '""');
              if (retryRole === 'client') {
                fetchCart(retryUserId);
              }
            } else {
              console.log('[CartContext] Retry failed, user info still not available');
            }
          }, 500);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('logout', handleLogout);
    window.addEventListener('login', handleLogin);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('logout', handleLogout);
      window.removeEventListener('login', handleLogin);
    };
  }, [getUserId, fetchCart]);

  // Listen for localStorage changes to update cart across tabs
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'cartItems') {
        const localCart = localStorage.getItem('cartItems');
        if (localCart) {
          try {
            const parsedCart = JSON.parse(localCart);
            // console.log(`[CartContext] Cart updated from storage: ${parsedCart.length} items`);
            setItems(parsedCart);
          } catch (parseError) {
            console.error('[CartContext] Failed to parse cart from storage:', parseError);
            setItems([]);
          }
        } else {
          // console.log('[CartContext] Cart cleared from storage');
          setItems([]);
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  return (
    <CartContext.Provider
      value={{
        items,
        partnerOrders,
        setPartnerOrders,
        activePartnerId,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        setActivePartner,
        updatePartnerPaymentMethod,
        clearPartnerCart,
        total,
        isLoading,
        partnerIds: Array.from(
          new Set(items.filter(i => i.product.partner_id).map(i => i.product.partner_id!))
        ),
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};